/**
 * The web app's side of members and stewards (docs/members.md "The clients"): the pure parts of
 * the gate, the request headers, the join link and the House screen's rules, kept free of the DOM
 * and of Preact so Node's test runner can check them (tests/members-ui.test.ts).
 */
import {
  HOUSEHOLD_NAME_MAX,
  INVITE_CODE_LENGTH,
  MEMBER_MESSAGES,
  householdNameError,
  householdPasswordError,
  isInviteCode,
  normalizeHouseholdName,
  normalizeInviteCode,
  passwordRepeatError,
  type Role,
} from "../shared/members.ts";
import { leavesNoStewardWithPeople } from "../shared/passkeys.ts";
import { normalizeName } from "../shared/peons.ts";
import type { RosterEntry } from "../shared/house-roster.ts";
import type { AuthState, Member } from "../shared/types";

/**
 * Which form the gate shows: signing in as a person with their own password (the first form,
 * docs/members.md "Personal passwords"), the household password, an invite code, or founding a new
 * household.
 */
export type GateMode = "person" | "password" | "invite" | "create";

/** The gate's fields; each form reads only its own. */
export interface GateFields {
  personName: string;
  personPassword: string;
  password: string;
  code: string;
  name: string;
  newPassword: string;
  again: string;
  creationCode: string;
}

export const EMPTY_GATE_FIELDS: GateFields = { personName: "", personPassword: "", password: "", code: "", name: "", newPassword: "", again: "", creationCode: "" };

/** What a gate form sends, or why it cannot be sent yet. */
export type GateSubmission =
  | { ok: true; path: "/api/auth/person/login"; body: { name: string; password: string } }
  | { ok: true; path: "/api/auth/login"; body: { password: string } }
  | { ok: true; path: "/api/auth/join"; body: { code: string } }
  | { ok: true; path: "/api/auth/create"; body: { name: string; password: string; code?: string } }
  | { ok: false; error: string | null };

/**
 * The request a gate form makes. `error` is null when the form is simply incomplete (the button
 * stays disabled), and a message when what was typed will not do. The create form checks the name,
 * the password and the repeat with the same rules as the server (shared/members.ts), and sends the
 * creation code only when the server asks for one.
 */
export function gateSubmission(mode: GateMode, fields: GateFields, createNeedsCode: boolean): GateSubmission {
  if (mode === "person") {
    const name = normalizeName(fields.personName);
    return name && fields.personPassword
      ? { ok: true, path: "/api/auth/person/login", body: { name, password: fields.personPassword } }
      : { ok: false, error: null };
  }
  if (mode === "password") {
    return fields.password ? { ok: true, path: "/api/auth/login", body: { password: fields.password } } : { ok: false, error: null };
  }
  if (mode === "invite") {
    const code = normalizeInviteCode(fields.code);
    return isInviteCode(code) ? { ok: true, path: "/api/auth/join", body: { code } } : { ok: false, error: null };
  }
  if (!fields.name.trim() || !fields.newPassword || !fields.again || (createNeedsCode && !fields.creationCode.trim())) {
    return { ok: false, error: null };
  }
  const error = householdNameError(fields.name) ?? householdPasswordError(fields.newPassword) ?? passwordRepeatError(fields.newPassword, fields.again);
  if (error) return { ok: false, error };
  const body: { name: string; password: string; code?: string } = { name: normalizeHouseholdName(fields.name), password: fields.newPassword };
  if (createNeedsCode) body.code = fields.creationCode.trim();
  return { ok: true, path: "/api/auth/create", body };
}

/** The invite field as the peon types it: uppercase, no spaces or dashes, at most eight characters. */
export function inviteFieldValue(input: string): string {
  return normalizeInviteCode(input).slice(0, INVITE_CODE_LENGTH);
}

/** The longest name the create and rename fields accept. */
export const NAME_MAX = HOUSEHOLD_NAME_MAX;

/**
 * The invite code in a `/join/<code>` path, normalized, or null for any other path. A code that is
 * not a valid code still comes back (cut to eight characters), so the gate can show it and the
 * server's refusal (MEMBER_MESSAGES.inviteNotValid).
 */
export function joinCodeFromPath(pathname: string): string | null {
  const m = /^\/join\/([^/]+)\/?$/.exec(pathname);
  if (!m) return null;
  let raw = m[1];
  try {
    raw = decodeURIComponent(raw);
  } catch {
    /* keep it raw */
  }
  const code = inviteFieldValue(raw);
  return code ? code : null;
}

/**
 * The headers every request carries (docs/members.md): the device's peon name, URI-encoded since a
 * header cannot hold every name raw, when it has one; and the platform. The native app sends the
 * same two names.
 */
export function memberHeaders(peonName: string | null, platform = "web"): Record<string, string> {
  const headers: Record<string, string> = { "x-platform": platform };
  const name = peonName?.trim();
  if (name) headers["x-peon-name"] = encodeURIComponent(name);
  return headers;
}

/** Whether this device may open the workshop: stewards only. */
export function canOpenWorkshop(auth: Pick<AuthState, "authenticated" | "household"> | null): boolean {
  return !!auth?.authenticated && auth.household?.role === "steward";
}

/** What the header peon says when a peon taps the logo five times. */
export const WORKSHOP_REFUSAL_LINE = "Stewards only. Me just peon.";

/**
 * The account sheet's "Sign out" (POST /api/auth/logout): this device leaves every household it is
 * signed into, not just the one it is acting in, and comes back through the gate.
 */
export const SIGN_OUT_LABEL = "Sign out";
export const SIGN_OUT_LINE = "This device leaves every household. Come back with a passkey, the password or an invite.";
export const SIGN_OUT_CONFIRM = "Sign out of Omie's Peons on this device?";

/**
 * Why a role change or sign-out in the House screen would be refused before asking the server:
 * the house keeps at least one steward, counting people and devices signed in as no one
 * (shared/passkeys.ts leavesNoStewardWithPeople). Null when it may go.
 */
export function memberChangeRefusal(
  members: ReadonlyArray<{ session_id: string; role: Role; person?: { id: string } | null }>,
  target: string,
  next: Role | "removed",
  people: ReadonlyArray<{ id: string; role: Role }> = [],
): string | null {
  return leavesNoStewardWithPeople(members, people, target, next) ? MEMBER_MESSAGES.needSteward : null;
}

/** The delete button wakes only when the typed name is the household's name, as the server compares it. */
export function deleteNameMatches(typed: string, householdName: string): boolean {
  return normalizeHouseholdName(typed) === householdName && householdName.length > 0;
}

/** "Peon" or "Steward", for an invite's or a device's row. */
export function roleWord(role: Role): string {
  return role === "steward" ? "Steward" : "Peon";
}

/** How a device reported itself, for the Devices list. */
export function platformWord(platform: string): string {
  switch (platform) {
    case "web":
      return "Web";
    case "ios":
      return "iPhone";
    case "android":
      return "Android";
    default:
      return "";
  }
}

/** An invite's uses: "Unused", "1 of 5 used". */
export function inviteUsesText(uses: number, maxUses: number): string {
  return uses === 0 && maxUses === 1 ? "Unused" : `${uses} of ${maxUses} used`;
}

/** The Web Share title for an invite (docs/members.md). */
export function inviteShareTitle(householdName: string): string {
  return `Join ${householdName} on Omie's Peons`;
}

/** The Members list (docs/members.md, 2026-10-02): a name with no device signed in, and the devices that never sent a name. */
export const NO_DEVICE_LINE = "No device signed in";
export const NO_NAME_HEAD = "No name yet";

/** A Members entry's head: its name, or "No name yet". */
export function rosterHead(entry: Pick<RosterEntry, "name">): string {
  return entry.name ?? NO_NAME_HEAD;
}

/** The role word by an entry's name, only when no device line of its own says it. */
export function rosterRoleWord(entry: Pick<RosterEntry, "role" | "members">): string | null {
  return entry.role && entry.members.length === 0 ? roleWord(entry.role) : null;
}

/** A device's line under its name: "Steward · Android · Now", "Peon · Web · Seen 3 h ago". */
export function deviceLine(m: Pick<Member, "role" | "platform" | "this_device">, ago: string): string {
  return [roleWord(m.role), platformWord(m.platform), m.this_device ? "Now" : `Seen ${ago}`].filter(Boolean).join(" · ");
}
